import React from 'react';
import { List, Stack, ListItem, IconButton, Typography, ListItemText } from '@mui/material';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import Tooltip from '../../../../components/Tooltip';
import {
  ParkingLot,
  ParkingLotName,
  ParkingLotSectionDriveIn,
  ParkingLotSectionSeasonsPrices,
  ParkingLotSectionWithReservation,
} from '../../../../domain/parking';
import { PARKING_LOT_CARD_CONFIGURATION_MAP } from './parking-lot-card.configuration';
import { Card, DescriptionStack, DetailsStack, LotBox } from './parkint-lot-card.styles';

interface Props {
  section: ParkingLotSectionWithReservation | ParkingLotSectionDriveIn;
  seasonsPrices: ParkingLotSectionSeasonsPrices;
  lots: ParkingLot[];
  selectedLotName?: ParkingLotName;
  total: number | null;
  onSelect: (lot: ParkingLot) => void;
}

export const ParkingLotCard = ({ section, seasonsPrices, lots, selectedLotName, total, onSelect }: Props) => {
  const { t } = useTranslation();

  const { header, subHeader, tooltipDetails } = PARKING_LOT_CARD_CONFIGURATION_MAP[section];

  return (
    <Card>
      <Stack>
        <DescriptionStack color={header.color} bgcolor={header.backgroundColor}>
          <Typography fontSize={14} fontWeight={600}>{t(header.i18n)}</Typography>
        </DescriptionStack>
        <DetailsStack>
          <Typography color="white" fontWeight={600}>
            {t(subHeader.i18n)}
          </Typography>
          <Stack direction="row" alignItems="center" gap={1}>
            <Typography color="white" fontWeight={600}>
              {t('details')}
            </Typography>
            <Tooltip
              enterTouchDelay={0}
              title={
                <List dense disablePadding>
                  {tooltipDetails.map((detail) => (
                    <ListItem disablePadding key={detail.i18n}>
                      <Typography variant="body1" component="span" mr={2}>
                        &bull;
                      </Typography>
                      <ListItemText primary={t(detail.i18n)} />
                    </ListItem>
                  ))}
                </List>
              }
              placement="top"
            >
              <IconButton>
                <InfoRoundedIcon sx={{ color: 'white' }} />
              </IconButton>
            </Tooltip>
          </Stack>
        </DetailsStack>

        {lots.length ? (
          <Stack padding="24px 16px" gap={2}>
            <Typography variant="body2" color="secondary.light" fontWeight={700}>
              {t('parking_service.select_a_lot')}
            </Typography>
            <Stack direction="row" flexWrap="wrap" gap={1.5}>
              {lots.map((lot) => (
                <LotBox key={lot.id} onClick={() => onSelect(lot)} isSelected={selectedLotName === lot.location_name}>
                  <Typography variant="caption" fontWeight={700}>
                    {t(lot.location_name)}
                  </Typography>
                </LotBox>
              ))}
            </Stack>
          </Stack>
        ) : null}
      </Stack>

      <Stack pt={2} pb={3} px={2}>
        <Stack gap={1.5}>
          <Stack flexDirection="row" justifyContent="space-between" gap={2}>
            <Typography color="secondary.light" fontSize={14}>
              {t('parking_service.parking_lots_seasons.low')}:
            </Typography>
            <Typography color="secondary.light" fontSize={14}>
              ${seasonsPrices.low.toFixed(2)} USD
            </Typography>
          </Stack>
          <Stack flexDirection="row" justifyContent="space-between" gap={2}>
            <Typography color="secondary.light" fontSize={14}>
              {t('parking_service.parking_lots_seasons.high')}:
            </Typography>
            <Typography color="secondary.light" fontSize={14}>
              ${seasonsPrices.high.toFixed(2)} USD
            </Typography>
          </Stack>
        </Stack>

        {total ? (
          <Stack direction="row" justifyContent="space-between" mt={2}>
            <Typography color="primary" fontSize={18} fontWeight={700}>
              {t('total')}:{' '}
            </Typography>
            <Typography color="primary" fontSize={18} fontWeight={700}>
              ${total.toFixed(2)} USD
            </Typography>
          </Stack>
        ) : null}
      </Stack>
    </Card>
  );
};
